import Button from '@/decodo/components/atoms/Button';
import Card from '@/decodo/components/atoms/Card';
import Heading from '@/decodo/components/atoms/Heading';
import { NextImageProps } from '@/decodo/components/atoms/Image';
import { Text } from '@/decodo/components/atoms/Text';
import AnimatedEl from '@/lib/motion';
import { cn } from '@/lib/utils';
import { gradientDecodoBorderStyling } from '@/styles/gradient';
import { animate, AnimatePresence, useMotionValue } from 'motion/react';
import { useEffect } from 'react';
import { Swiper, SwiperEvents } from 'swiper/types';
import { carouselConfig } from '../constants/carousel';

export type CarouselCardProps = {
  swiper?: Swiper;
  slides: {
    title: string;
    description?: React.ReactNode;
    badge: React.ReactNode;
    image: NextImageProps;
  }[];
  activeSlideIndex: number;
};

export function CarouselCard<T>({
  swiper,
  slides,
  activeSlideIndex,
}: CarouselCardProps) {
  const activeDotX = useMotionValue(0);

  const lineScaleX = useMotionValue(0);

  const handleAutoplayTimeChange: SwiperEvents['autoplayTimeLeft'] = (
    _,
    __,
    percentageLeft
  ) => {
    lineScaleX.set(1 - percentageLeft);
  };

  useEffect(() => {
    animate(activeDotX, activeSlideIndex * (dotsGap + dotSize), {
      duration: textChangeDuration,
    });
  }, [activeSlideIndex]);

  useEffect(() => {
    swiper?.on('autoplayTimeLeft', handleAutoplayTimeChange);
    return () => {
      swiper?.off('autoplayTimeLeft', handleAutoplayTimeChange);
    };
  }, [swiper, lineScaleX]);

  const slide = slides[activeSlideIndex];

  const textChangeDuration = carouselConfig.speed / 2000;

  return (
    <Card
      color='neutral'
      roudnessLevel='s'
      padSize='l'
      classNames={{
        root: cn(
          'dark:bg-gray-50 dark:text-base-white',
          'bg-base-white text-gray-950',
          'gap-y-4 flex flex-col',
          gradientDecodoBorderStyling({
            borderWidth: 'p-px',
            color: 'gradient-main',
            borderRadius: 'rounded',
          })
        ),
      }}
    >
      <div className='flex flex-col gap-y-3'>
        <div className='overflow-hidden'>
          <AnimatePresence mode='wait'>
            <AnimatedEl
              el='div'
              key={activeSlideIndex}
              transition={{ duration: textChangeDuration }}
              {...textVariants}
            >
              {slide.badge}
            </AnimatedEl>
          </AnimatePresence>
        </div>
        <div className='overflow-hidden'>
          <AnimatePresence mode='wait'>
            <AnimatedEl
              el='div'
              key={activeSlideIndex}
              transition={{ duration: textChangeDuration }}
              {...textVariants}
            >
              <Heading level='h3'>{slide.title}</Heading>
            </AnimatedEl>
          </AnimatePresence>
        </div>
      </div>
      <div className='overflow-hidden'>
        <AnimatePresence mode='wait'>
          <AnimatedEl
            el='div'
            key={activeSlideIndex}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                delay: textChangeDuration,
                duration: textChangeDuration,
              },
            }}
            exit={{ opacity: 0 }}
          >
            <Text>{slide.description}</Text>
          </AnimatedEl>
        </AnimatePresence>
      </div>
      <div className='mt-5 flex items-center gap-x-6'>
        <div className='flex gap-x-3'>
          {slides.map((_, i) => (
            <Button
              key={i}
              noStyles
              classNames={{
                root: cn(
                  'h-2 w-2 bg-gray-200 rounded-full duration-150',
                  i !== activeSlideIndex && 'hover:bg-gray-500'
                ),
              }}
              onClick={() => swiper?.slideTo(i)}
            >
              <div className='sr-only'>Go to slide {i + 1}</div>
            </Button>
          ))}
          <AnimatedEl
            el='div'
            className='bg-base-black absolute z-[1] h-2 w-2 rounded-full'
            style={{ x: activeDotX }}
          />
        </div>
        <div className='flex h-px flex-grow bg-gray-200'>
          <AnimatedEl
            el='div'
            className={cn('bg-base-black h-full w-full origin-left')}
            style={{ scaleX: lineScaleX }}
            transition={{ delay: textChangeDuration }}
          />
        </div>
      </div>
    </Card>
  );
}

const dotSize = 8;
const dotsGap = 12;

const textVariants = {
  initial: { y: '100%', opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: '100%', opacity: 0 },
};
